import React, { FC, useState, Dispatch, SetStateAction, useEffect, useContext } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';

import SelectInput from '../../../sharedModules/form/components/SelectInput';
import TextInput from '../../../sharedModules/form/components/TextInput';
import axios from '../../../sharedModules/utils/axios.utils';

import { InstallationCostRecord } from '../../estimateDetails/model';

interface InstallationCostModalProps {
  newModalOpen: boolean;
  setNewModalOpen: Dispatch<SetStateAction<boolean>>;
  title: string;
  unitLabel: string;
  descriptionOptions?: any[];
}

const InstallationCostModal: FC<InstallationCostModalProps> = (props) => {
  let { newModalOpen, setNewModalOpen, title, unitLabel, descriptionOptions = [] } = props;

  let [formValues, setFormValues] = useState<InstallationCostRecord>({});
  let [materialCategory, setMaterialCategory] = useState('');
  let [totalCost, setTotalCost] = useState(0);
  let [estimateAssetChoices, setEstimateAssetChoices] = useState([]);

  let { salesEstimate, setSalesEstimate } = useContext(EstimateDetailsContext);

  const rateWithOvertime = () => {
    return formValues?.overtime ? parseFloat(formValues?.unit_cost) * 1.5 : formValues?.unit_cost;
  };

  useEffect(() => {
    let total = parseFloat(formValues.unit_cost) * parseFloat(formValues.job_units) || 0;
    setTotalCost(total);
  }, [formValues]);

  useEffect(() => {
    let choices = salesEstimate.sales_estimate_assets?.flatMap((asset) => ({
      label: asset.asset_number,
      value: asset.id,
    }));
    setEstimateAssetChoices(choices);
    let defaultAssetId = choices?.[0]?.value;

    let defaultCostPerUnit = 0;
    if (title === 'Labor') defaultCostPerUnit = 100;

    setFormValues({
      ...formValues,
      sales_estimate_id: salesEstimate?.id,
      sales_estimate_asset_id: defaultAssetId?.toString(),
      unit_cost: defaultCostPerUnit?.toString(),
    });
  }, [title]);

  // Update total cost independently to avoid circular re-rendering.
  useEffect(() => {
    const total = parseFloat(formValues?.unit_cost) * parseFloat(formValues?.job_units || '0');
    const overtimeFactor = formValues?.overtime ? 1.5 : 1;
    setTotalCost(total * overtimeFactor);
  }, [formValues?.unit_cost, formValues?.job_units, formValues?.overtime]);

  let handleSubmit = async () => {
    let reponse: any = await axios.post('/sales_estimates/installation_cost_records', {
      installation_cost_record: formValues,
    });
    setSalesEstimate(reponse.data.result);
    setNewModalOpen(false);
  };

  return (
    <Dialog open={newModalOpen} onClose={() => setNewModalOpen(false)}>
      <DialogTitle>Add New {title} Cost</DialogTitle>
      <DialogContent style={{ width: 400 }}>
        <SelectInput
          label="Asset Number"
          value={formValues.sales_estimate_asset_id}
          valueChanged={(value) => setFormValues({ ...formValues, sales_estimate_asset_id: value })}
          choices={[...estimateAssetChoices, { label: 'None', value: '0' }]}
        />
        <br />
        <SelectInput
          label="Description"
          value={formValues.installation_cost_id}
          valueChanged={(value) => {
            setFormValues({ ...formValues, installation_cost_id: value });
            setMaterialCategory(descriptionOptions.filter((option) => option.value === value)[0]?.label);
          }}
          choices={descriptionOptions}
        />
        <br />
        {materialCategory === 'Other' && (
          <>
            <TextInput
              label="Other Description"
              value={formValues.description_override}
              shrinkLabel
              valueChanged={(value) => setFormValues({ ...formValues, description_override: value })}
            />
            <br />
          </>
        )}
        <TextInput
          label={`Cost per ${unitLabel}`}
          value={formValues.unit_cost}
          shrinkLabel
          currency
          valueChanged={(value) => setFormValues({ ...formValues, unit_cost: value })}
        />
        <br />
        <TextInput
          label={`${unitLabel}s`}
          value={formValues.job_units}
          shrinkLabel
          valueChanged={(value) => setFormValues({ ...formValues, job_units: value })}
        />
        <br />
        {title === 'Labor' && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues?.overtime}
                  onChange={(e) => setFormValues({ ...formValues, overtime: e.target.checked })}
                />
              }
              label="Overtime"
            />
            {formValues?.overtime && (
              <Typography style={{ marginTop: 10 }}>Rate with Overtime: ${rateWithOvertime()}</Typography>
            )}
          </>
        )}
        <Typography style={{ marginTop: 10 }}>Total Cost: ${totalCost?.toFixed(2) || ''}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNewModalOpen(false)}>Cancel</Button>
        <Button type="submit" onClick={handleSubmit}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstallationCostModal;
